import { RefObject } from 'react';
import {
  AiGenerationData,
  AiGenerationStatus,
  ChatsTasksFilter,
  Choice,
  Choices,
  ChoicesCard,
  ConversationRole,
  ConvertedImageGenData,
  ExternalModelReference,
  ExternalModelTabContent,
  GroupedMessagesByDate,
  isChoices,
  isMessageWithCreationTaskId,
  isObject,
  Message,
  MessageForRender,
  MessageSkill,
  MessageType,
  QuickSkillCommand,
  TaskSkill,
  TaskStatusFilter,
} from 'src/types';
import { uppercaseFirstLetter } from './general';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
  DEFAULT_SCROLLER_THRESHOLD,
  externalModelsSet,
  FILE_REGEX,
  TEMPORARY_CONVERSATION_ID,
  TEMPORARY_EMPTY_MESSAGE_ID,
} from 'src/constants';
import type { ImageGenData } from 'src/types/models/ImageGenData';
import { UserTier } from 'src/types/models/UserTier';
import { getModelDisplayName, getModelIcon } from './externalModels';
import type { ModelMetadata } from 'src/types/models/ModelMetadata';
import {
  ItemLocationWithAlign,
  VirtuosoMessageListMethods,
} from '@virtuoso.dev/message-list';
import { env } from 'src/env';

dayjs.extend(utc);

/**
 * getReadableKey() returns readable key.
 * @param key string
 * @returns string
 */
export function getReadableKey(key: string): string {
  return key
    .split('_')
    .map((word) => uppercaseFirstLetter(word))
    .join(' ');
}

/**
 * updateOptionSelectedValueForKey() deeply updates object.
 * @param obj ChoicesCard|Choices|Choice
 * @param selectKey string
 * @param selectValue string
 * @param selected boolean
 */
export function updateOptionSelectedValueForKey(
  obj: Choice | Choices | ChoicesCard,
  selectKey: string,
  selectValue: string,
  selected: boolean,
  multiSelect: boolean,
): Choice | Choices | ChoicesCard {
  if (!isObject(obj) || obj === null) {
    return obj;
  }

  // Check if obj is Choices and has choice_list property
  if ('choice_list' in obj && obj.select_key === selectKey && isChoices(obj)) {
    const updatedChoiceList = (obj?.choice_list || []).map((choice) => {
      if (choice.select_value === selectValue) {
        return { ...choice, selected };
      } else if (!multiSelect && selected) {
        return { ...choice, selected: false };
      }
      return choice;
    });

    // Create a new object with the updated choice_list
    return { ...obj, choice_list: updatedChoiceList };
  }

  // If obj is Choice or other types, recursively update
  const updatedObj = Array.isArray(obj) ? [] : {};
  for (const [key, value] of Object.entries(obj)) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (isObject(value)) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      updatedObj[key] = updateOptionSelectedValueForKey(
        value,
        selectKey,
        selectValue,
        selected,
        multiSelect,
      );
    } else {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      updatedObj[key] = value;
    }
  }

  return updatedObj;
}

/**
 * addEmptyWaitingMessage() adds empty message at the end of the messages array
 * and removes not needed messages, also ignores them. Used in the Flat dasign
 * @param messages Array<Message>
 * @param ignoreMessages boolean
 * @returns Array<Message>
 */
export function addEmptyWaitingMessage(
  messages: Message[],
  ignoreMessages = false,
): Message[] {
  const lastMessage = messages?.at(-1);
  if (lastMessage) {
    return lastMessage?.role === ConversationRole.USER && !ignoreMessages
      ? [
          ...messages,
          {
            message_id: TEMPORARY_EMPTY_MESSAGE_ID,
            content: '',
            role: ConversationRole.AGENT,
            user_id: lastMessage.user_id,
            from_user_id: lastMessage.to_user_id,
            to_user_id: lastMessage.from_user_id,
          },
        ]
      : messages;
  }
  return messages;
}

export const removeHashFromString = (text: string) => {
  return text.replace('#', '');
};

export const getTaskIdFromMessage = (message?: Message): string | undefined => {
  if (!message) {
    return;
  }

  if (isMessageWithCreationTaskId(message)) {
    return message?.payload?.task_id;
  }
  if (message.message_type === MessageType.TASK_CREATED) {
    return message?.task_id || undefined;
  }
};

export const prepareMessagesToRender = (
  messages: Message[],
): Array<{ userMessage: MessageForRender; agentMessage: MessageForRender }> => {
  const updatedMessages = messages.reduce<
    Array<{ userMessage: MessageForRender; agentMessage: MessageForRender }>
  >((acc, message, index, array) => {
    const nextMessage = array[index + 1];

    if (
      message.role === ConversationRole.USER &&
      nextMessage?.role === ConversationRole.AGENT
    ) {
      const previousMessage = array[index - 1];

      const showDate =
        index === 0 ||
        new Date(message.timestamp || '').toLocaleDateString() !==
          new Date(previousMessage?.timestamp || '').toLocaleDateString();

      const anchorId = getTaskIdFromMessage(nextMessage);

      const userMessage: MessageForRender = {
        ...message,
        showDate,
        anchorId,
      };

      acc.push({ userMessage, agentMessage: nextMessage });
    }

    return acc;
  }, []);

  return updatedMessages;
};

export const getSkillFromTask = (taskSkill: TaskSkill): MessageSkill => {
  switch (taskSkill) {
    case TaskSkill.SCHEDULING:
      return MessageSkill.SCHEDULER;
    case TaskSkill.RESEARCH:
      return MessageSkill.RESEARCHER;
    case TaskSkill.CODING:
      return MessageSkill.CODER;

    default:
      return MessageSkill.WRITER;
  }
};

export const groupMessagesByDate = (
  messages: Message[],
): GroupedMessagesByDate => {
  const groupedMessages: GroupedMessagesByDate = {};

  messages.forEach((message) => {
    const formattedDate = dayjs(message?.timestamp).format('MMMM D, YYYY');

    groupedMessages[formattedDate] = groupedMessages[formattedDate] || [];
    groupedMessages[formattedDate].push(message);
  });

  return groupedMessages;
};

export const onScrollToMessageByTask = (taskId: string) => {
  const focusTaskElement = document.getElementById(taskId);

  if (focusTaskElement) {
    focusTaskElement?.scrollIntoView({ behavior: 'smooth' });
  }
};

export const getReadableTaskSkillName = (skill?: TaskSkill | string) => {
  switch (skill) {
    case TaskSkill.RESEARCH:
      return 'researcher';
    case TaskSkill.SCHEDULING:
      return 'scheduler';
    case TaskSkill.CODING:
      return 'coder';
    case TaskSkill.CHITCHAT:
      return 'writer';
    case TaskSkill.IMAGE:
      return 'image';
    case TaskSkill.VIDEO:
      return 'video';
    case TaskSkill.DOCUMENT_QA:
      return 'file';
    case TaskSkill.DEEP_RESEARCH:
      return 'researcher-deep';
    default:
      return '';
  }
};

/**
 * @deprecated
 */
export const prepareExternalModelsReferences = (
  references: Record<string, AiGenerationData>,
  originalQuery?: string,
): ExternalModelReference[] => {
  return Object.entries(references)
    .filter(([_, { model }]) => model && externalModelsSet[model])
    .map(([key, { content, model }]) => {
      return {
        modelKey: key,
        content: content || '',
        icon_name: model ? externalModelsSet[model].icon_name : null,
        title: model ? externalModelsSet[model].display_name : '',
        url: model || '',
        originalQuery: originalQuery?.replace(FILE_REGEX, ''),
      };
    });
};

export const prepareExternalModelsTabContent = ({
  references,
  metadata,
}: {
  references: Record<string, AiGenerationData>;
  metadata?: Record<string, ModelMetadata>;
}): ExternalModelTabContent[] => {
  return Object.values(references)
    .filter(({ model }) => model)
    .map(({ content, model, status, reasoning }) => {
      return {
        content: content || '',
        iconName: getModelIcon({ targetModel: model, metadata: metadata }),
        tabTitle: getModelDisplayName({
          targetModel: model,
          metadata: metadata,
        }),
        tabName: model || '',
        status: status || undefined,
        reasoning: reasoning || undefined,
      };
    });
};

export const prepareGeneratedImages = ({
  images,
  tierId,
  withWatermark,
  metadata,
}: {
  images: Record<string, ImageGenData>;
  tierId: UserTier;
  withWatermark?: boolean;
  metadata?: Record<string, ModelMetadata>;
}) => {
  const isOpenTier = tierId === UserTier.OPEN;
  const isFreeTier = tierId === UserTier.FREE;

  const models = Object.values(images).reduce(
    (acc, item) => {
      if (!!item.model && !acc[item.model]) {
        acc[item.model] = [];
      }
      if (item.model) {
        acc[item.model].push(Object.assign({}, item));
      }
      return acc;
    },
    {} as Record<string, Array<ConvertedImageGenData>>,
  );

  Object.keys(models).forEach((model) => {
    models[model].forEach((item, index) => {
      if (index === (isOpenTier ? 1 : 2) || index === 3) {
        item.isDisabled = true;
      }
    });
  });

  const result = Object.keys(images).reduce(
    (acc, key) => {
      acc[key] = models[images[key].model as string]?.find(
        (item) => item.image_url === images[key].image_url,
      );
      return acc;
    },
    {} as Record<string, ConvertedImageGenData | undefined>,
  );

  return Object.entries(
    (!isOpenTier && !isFreeTier ? images : result) as Record<
      string,
      ConvertedImageGenData | undefined
    >,
  ).map(([key, image]) => {
    return {
      id: key,
      url: image?.image_url || '',
      icon: getModelIcon({ targetModel: image?.model, metadata: metadata }),
      title: getModelDisplayName({
        targetModel: image?.model,
        metadata: metadata,
      }),
      height: 200,
      width: 200,
      source: image?.model || '',
      status: image?.status || AiGenerationStatus.SUCCESS, // default status
      isDisabled: image?.isDisabled,
      size: image?.size,
      withWatermark: withWatermark,
    };
  });
};

export const getTasksFilterFromChatsFilter = (
  filter: TaskStatusFilter,
): ChatsTasksFilter | undefined => {
  switch (filter) {
    case TaskStatusFilter.BLOCKED:
      return ChatsTasksFilter.BLOCKED;
    case TaskStatusFilter.DONE:
      return ChatsTasksFilter.DONE;
    case TaskStatusFilter.IN_PROGRESS:
      return ChatsTasksFilter.IN_PROGRESS;
    case TaskStatusFilter.FAILED:
      return ChatsTasksFilter.FAILED;
    case TaskStatusFilter.STOPPED:
      return ChatsTasksFilter.STOPPED;
    default:
      return undefined;
  }
};

export const removeQuickSkillCommandPrefix = (input: string) => {
  const command = Object.values(QuickSkillCommand).find((cmd) =>
    input.startsWith(cmd),
  );
  return command ? input.slice(command.length).trim() : input;
};

export const extractQuickSkillCommand = (input: string) => {
  const command = Object.values(QuickSkillCommand).find((cmd) =>
    input.startsWith(cmd),
  );
  const cleanedInput = command ? input.slice(command.length).trim() : input;

  return {
    command,
    cleanedInput,
  };
};

export const getMessageListRefForPDU = ({
  conversationId,
  mainListRef,
  compareListRef,
}: {
  conversationId: Message['conversation_id'];
  mainListRef: RefObject<VirtuosoMessageListMethods<Message>>;
  compareListRef: RefObject<VirtuosoMessageListMethods<Message>>;
}): RefObject<VirtuosoMessageListMethods<Message>> | null => {
  if (conversationId?.includes(TEMPORARY_CONVERSATION_ID)) {
    return mainListRef;
  }

  const mainRefConversationId =
    mainListRef.current?.data.get()?.[0]?.conversation_id;
  const compareRefConversationId =
    compareListRef.current?.data.get()?.[0]?.conversation_id;

  if (!!mainRefConversationId && mainRefConversationId === conversationId) {
    return mainListRef;
  }

  if (
    !!compareRefConversationId &&
    compareRefConversationId === conversationId
  ) {
    return compareListRef;
  }

  return null;
};

export const validateMessageListRef = ({
  conversationId,
  ref,
}: {
  conversationId: Message['conversation_id'];
  ref: RefObject<VirtuosoMessageListMethods<Message>>;
}) => {
  if (conversationId?.includes(TEMPORARY_CONVERSATION_ID)) {
    return true;
  }

  const refConversationId = ref.current?.data.get()?.[0]?.conversation_id;

  if (!refConversationId) {
    return true;
  }

  return refConversationId === conversationId;
};

export const shouldScrollToBottom = (
  listRef: RefObject<VirtuosoMessageListMethods<Message>>,
) => {
  const scrollLocation = listRef.current?.getScrollLocation();

  if (!scrollLocation) {
    return false;
  }

  return scrollLocation.bottomOffset < DEFAULT_SCROLLER_THRESHOLD;
};

export const isLastMessage = (
  listRef: RefObject<VirtuosoMessageListMethods<Message>>,
  messageId: Message['message_id'],
) => {
  const items = listRef.current?.data.get() ?? [];
  return items.length > 0 && items[items.length - 1].message_id === messageId;
};

export const autoscrollToBottom = (
  listRef: RefObject<VirtuosoMessageListMethods<Message>>,
  messageId: Message['message_id'],
) => {
  return {
    location: (): ItemLocationWithAlign | null => {
      if (shouldScrollToBottom(listRef) && isLastMessage(listRef, messageId)) {
        return {
          index: 'LAST',
          align: 'end',
          behavior: 'smooth',
        };
      }

      return null;
    },
  } as const;
};

export const getMessageKey = (key = env.REACT_APP_MESSAGE_KEY): string => {
  return atob(key).split('').reverse().join('');
};
